// client/src/components/AccountSwitcher.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FiChevronDown, FiTrash2 } from 'react-icons/fi';
import { auth, secondaryAuth, firestore } from '../../firebase';
import { sanitize } from '../../utils/sanitize';
import {
  MAX_LINKED_ACCOUNTS,
  readLinkedAccounts,
  persistLinkedAccounts,
  getLinkedAccountsStorageKey,
  decodeSecret,
  upsertLinkedAccountWithPassword,
  patchLinkedAccount,
  deleteLinkedAccount,
  saveRemoteLinkedAccounts,
  subscribeRemoteLinkedAccounts,
} from '../../utils/accountLinkStorage';
import '../CSS/AccountSwitcher.css';

const roleToRoute = (roleRaw) => {
  const role = (roleRaw || '').toLowerCase();
  switch (role) {
    case 'client':
      return '/clientdocs';
    case 'commercant':
      return '/merchant-dashboard';
    case 'commercantpending':
      return '/merchantprocesspaiement';
    case 'affiliatepending':
      return '/affiliatespending';
    case 'affiliates':
    case 'affiliateregie':
      return '/affiliatesdashboard';
    case 'admin':
      return '/admin-dashboard';
    case 'cashier':
      return '/cashierdashboard';
    default:
      return '/';
  }
};

const AccountSwitcher = ({ label, labelStyle = {}, align = 'center' }) => {
  // eslint-disable-next-line
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [currentUid, setCurrentUid] = useState(auth.currentUser?.uid || null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [shareBack, setShareBack] = useState(true);
  const [switchingUid, setSwitchingUid] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const remoteUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || null);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUid) {
      setAccounts([]);
      return;
    }
    setAccounts(readLinkedAccounts(currentUid));
  }, [currentUid]);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') return undefined;
    const handleClick = (event) => {
      const target = event.target;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen && globalError) {
      setGlobalError('');
    }
  }, [menuOpen, globalError]);

  const syncOwnerAccounts = useCallback(async (nextList) => {
    const owner = currentUid || auth.currentUser?.uid;
    if (!owner) return;
    setAccounts(nextList);
    persistLinkedAccounts(owner, nextList);
    try {
      // Évite une erreur de permission si l'UID connecté ne correspond plus au propriétaire
      if (auth.currentUser?.uid === owner) {
        await saveRemoteLinkedAccounts(owner, nextList);
      }
    } catch (err) {
      console.error('[AccountSwitcher] syncOwnerAccounts error:', err);
    }
  }, [currentUid]);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const handleStorage = (evt) => {
      if (!currentUid) return;
      if (evt.key === getLinkedAccountsStorageKey(currentUid)) {
        setAccounts(readLinkedAccounts(currentUid));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [currentUid]);

  useEffect(() => {
    if (remoteUnsubRef.current) {
      remoteUnsubRef.current();
      remoteUnsubRef.current = null;
    }
    if (!currentUid) return () => {};
    const unsubscribe = subscribeRemoteLinkedAccounts(currentUid, (remoteAccounts, exists) => {
      const hasRemoteAssociations = remoteAccounts.some((acc) => acc.uid !== currentUid);
      if (!exists || !hasRemoteAssociations) {
        if (exists && !hasRemoteAssociations && remoteAccounts.length) {
          saveRemoteLinkedAccounts(currentUid, remoteAccounts).catch((err) => {
            console.error('[AccountSwitcher] cleanup remote links error:', err);
          });
        }
        const cached = readLinkedAccounts(currentUid);
        const hasCachedAssociations = cached.some((acc) => acc.uid !== currentUid);
        if (hasCachedAssociations) {
          saveRemoteLinkedAccounts(currentUid, cached).catch((err) => {
            console.error('[AccountSwitcher] seed remote error:', err);
          });
        }
        return;
      }
      setAccounts(remoteAccounts);
      persistLinkedAccounts(currentUid, remoteAccounts);
    });
    remoteUnsubRef.current = unsubscribe;
    return () => {
      unsubscribe && unsubscribe();
      remoteUnsubRef.current = null;
    };
  }, [currentUid]);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0));
  }, [accounts]);

  const handleRemoveAccount = async (uid) => {
    if (!currentUid) return;
    const target = accounts.find((acc) => acc.uid === uid);
    if (!target) return;
    const confirmMsg = `Supprimer le compte "${target.pseudo || target.email}" de cette liste ?`;
    if (!window.confirm(confirmMsg)) return;

    const next = deleteLinkedAccount(currentUid, uid).accounts;
    await syncOwnerAccounts(next);
  };

  const refreshAccountMeta = async (uid) => {
    try {
      const snap = await getDoc(doc(firestore, 'users', uid));
      if (!snap.exists()) return null;
      const data = snap.data();
      return {
        pseudo: data?.pseudo || '',
        role: data?.role || '',
      };
    } catch (error) {
      console.error('[AccountSwitcher] refreshAccountMeta error:', error);
      return null;
    }
  };

  const updateAccountEntry = (uid, patch) => {
    const owner = currentUid || auth.currentUser?.uid;
    if (!owner) return;
    const res = patchLinkedAccount(owner, uid, patch);
    if (!res.ok) return;
    syncOwnerAccounts(res.accounts).catch(() => {});
  };

  const handleSwitchAccount = async (account) => {
    if (!account?.secret) {
      setGlobalError("Impossible d'utiliser ce compte. Supprimez-le puis ajoutez-le à nouveau.");
      return;
    }
    setSwitchingUid(account.uid);
    setGlobalError('');
    try {
      // Met à jour l'ordre/lastUsed tant qu'on est encore connecté avec le compte propriétaire (UID courant)
      updateAccountEntry(account.uid, { lastUsedAt: Date.now() });

      // Stop remote listener before l’auth bascule pour éviter le 403 Firestore sur l’ancien doc
      if (remoteUnsubRef.current) {
        remoteUnsubRef.current();
        remoteUnsubRef.current = null;
      }
      setAccounts([]);

      const pwd = decodeSecret(account.secret);
      const credential = await signInWithEmailAndPassword(auth, account.email, pwd);
      let role = account.role;
      let pseudo = account.pseudo;
      if (!role || !pseudo) {
        const meta = await refreshAccountMeta(credential.user.uid);
        if (meta) {
          role = role || meta.role;
          pseudo = pseudo || meta.pseudo || account.email;
        }
      }
      localStorage.setItem('userEmail', credential.user.email || account.email);
      if (role) localStorage.setItem('userRole', role);
      const targetRoute = roleToRoute(role);
      setMenuOpen(false);
      // Recharge complète pour nettoyer tous les listeners Firestore de l'ancien compte
      window.location.assign(targetRoute || '/');
    } catch (error) {
      console.error('[AccountSwitcher] switch error:', error);
      let message = "Impossible de se connecter avec ce compte.";
      if (error.code === 'auth/wrong-password') message = 'Mot de passe obsolète. Supprimez puis ajoutez à nouveau ce compte.';
      if (error.code === 'auth/user-not-found') message = 'Compte introuvable. Supprimez-le de la liste.';
      if (error.code === 'auth/too-many-requests') message = 'Trop de tentatives. Veuillez patienter avant de réessayer.';
      setGlobalError(message);
    } finally {
      setSwitchingUid('');
    }
  };

  const handleSubmitNewAccount = async (evt) => {
    evt.preventDefault();
    setModalError('');
    const email = sanitize(modalEmail).trim();
    const password = modalPassword.trim();
    if (!email || !password) {
      setModalError('Email et mot de passe requis.');
      return;
    }
    setIsSubmitting(true);
    try {
      const owner = auth.currentUser;
      if (!owner?.uid || !owner.email) {
        setModalError("Impossible d'enregistrer votre compte actuel. Reconnectez-vous puis réessayez.");
        setIsSubmitting(false);
        return;
      }
      if (!currentUid) {
        setModalError("Session invalide. Reconnectez-vous.");
        setIsSubmitting(false);
        return;
      }


      let selfEntry = accounts.find((acc) => acc.uid === owner.uid && acc.secret);
      if (!selfEntry && currentUid) {
        const localList = readLinkedAccounts(currentUid);
        selfEntry = localList.find((acc) => acc.uid === owner.uid && acc.secret);
      }
      const ownerPasswordPlain = selfEntry?.secret ? decodeSecret(selfEntry.secret) : '';
      if (!ownerPasswordPlain) {
        setModalError('Session expirée. Déconnectez-vous puis reconnectez-vous pour associer un compte.');
        setIsSubmitting(false);
        return;
      }
      if (!ownerPasswordPlain) {
        setModalError('Impossible de récupérer votre mot de passe. Merci de le saisir à nouveau.');
        setIsSubmitting(false);
        return;
      }

      const credential = await signInWithEmailAndPassword(secondaryAuth, email, password);
      const { user } = credential;
      // On ne lit pas /users/{uid} ici car les règles interdisent la lecture d'un autre compte.
      // Le rôle sera complété plus tard lors de la première connexion de ce compte.
      let pseudo = user.displayName || email;
      let role = '';

      const result = upsertLinkedAccountWithPassword({
        ownerUid: currentUid,
        uid: user.uid,
        email,
        password,
        pseudo,
        role,
      });
      if (!result.ok && result.error === 'limit') {
        setModalError('Limite de 10 comptes associée. Retirez-en un avant d’en ajouter un nouveau.');
        return;
      }
      if (!result.ok) {
        setModalError("Impossible d'associer ce compte pour le moment.");
        return;
      }
      await syncOwnerAccounts(result.accounts);

      setModalOpen(false);
      setModalPassword('');
    } catch (error) {
      console.error('[AccountSwitcher] add error:', error);
      let message = "Impossible d'associer ce compte.";
      if (error.code === 'auth/wrong-password') message = 'Mot de passe incorrect.';
      if (error.code === 'auth/user-not-found') message = 'Compte introuvable.';
      if (error.code === 'auth/too-many-requests') message = 'Trop de tentatives. Merci de patienter puis réessayer.';
      setModalError(message);
    } finally {
      try {
        await signOut(secondaryAuth);
      } catch {}
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setMenuOpen(false);
    setModalError('');
    setModalEmail('');
    setModalPassword('');
    setShareBack(true);
    setModalOpen(true);
  };

  const alignClass = align === 'left' ? 'account-switcher--left' : align === 'right' ? 'account-switcher--right' : 'account-switcher--center';

  return (
    <>
      <div className={`account-switcher ${alignClass}`}>
        <button
          type="button"
          className="account-switcher__anchor"
          style={labelStyle}
          onClick={() => setMenuOpen((prev) => !prev)}
          ref={anchorRef}
        >
          <span className="account-switcher__label">
            {label || auth.currentUser?.email || 'Compte'}
          </span>
          <FiChevronDown className={`account-switcher__chevron ${menuOpen ? 'is-open' : ''}`} />
        </button>

        {menuOpen && (
          <div className="account-switcher__menu" ref={menuRef}>
            <div className="account-switcher__head">
              <strong>Comptes associés</strong>
              <span>{accounts.length}/{MAX_LINKED_ACCOUNTS}</span>
            </div>

            {globalError && (
              <p className="account-switcher__error" role="alert">{globalError}</p>
            )}

            <div className="account-switcher__list">
              {sortedAccounts.length === 0 && (
                <p className="account-switcher__empty">Aucun compte enregistré pour l’instant.</p>
              )}

              {sortedAccounts.map((account) => {
                const isCurrent = currentUid === account.uid;
                const isLoading = switchingUid === account.uid;
                return (
                  <div key={account.uid} className={`account-switcher__item ${isCurrent ? 'is-current' : ''}`}>
                    <button
                      type="button"
                      className="account-switcher__item-btn"
                      onClick={() => handleSwitchAccount(account)}
                      disabled={isCurrent || isLoading}
                    >
                      <span className="account-switcher__item-main">
                        <span className="account-switcher__item-pseudo">{account.pseudo || account.email}</span>
                        <span className="account-switcher__item-email">{account.email}</span>
                      </span>
                      {isCurrent && <span className="account-switcher__badge">Actuel</span>}
                      {isLoading && <span className="account-switcher__badge">...</span>}
                    </button>
                    <button
                      type="button"
                      className="account-switcher__remove"
                      onClick={() => handleRemoveAccount(account.uid)}
                      title="Retirer ce compte"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="account-switcher__cta">
              <button
                type="button"
                className="account-switcher__add-btn"
                onClick={handleOpenModal}
              >
                Associer un compte
              </button>
            </div>

            {accounts.length >= MAX_LINKED_ACCOUNTS && (
              <p className="account-switcher__info">
                Limite atteinte : retirez un compte pour en ajouter un nouveau.
              </p>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="account-switcher__modal-backdrop">
          <div className="account-switcher__modal">
            <button
              type="button"
              className="account-switcher__modal-close"
              onClick={() => setModalOpen(false)}
              aria-label="Fermer"
            >
              &times;
            </button>
            <h4>Associer un compte existant</h4>
            <p className="account-switcher__modal-desc">
              Entrer les identifiants d’un autre compte Ecobill pour passer de l’un à l’autre en un clic.
            </p>
            <form onSubmit={handleSubmitNewAccount}>
              <label className="account-switcher__field">
                Email
                <input
                  type="email"
                  value={modalEmail}
                  onChange={(e) => setModalEmail(e.target.value)}
                  required
                />
              </label>
              <label className="account-switcher__field">
                Mot de passe
                <input
                  type="password"
                  value={modalPassword}
                  onChange={(e) => setModalPassword(e.target.value)}
                  required
                />
              </label>
              <label className="account-switcher__toggle">
                <input
                  type="checkbox"
                  checked={shareBack}
                  onChange={(e) => setShareBack(e.target.checked)}
                />
                <span>Autoriser ce compte à revenir sur le mien</span>
              </label>
              {modalError && <p className="account-switcher__error" role="alert">{modalError}</p>}
              <button type="submit" className="account-switcher__submit" disabled={isSubmitting}>
                {isSubmitting ? 'Association…' : 'Associer ce compte'}
              </button>
            </form>
            <p className="account-switcher__note">
              Maximum {MAX_LINKED_ACCOUNTS} comptes peuvent être associés. Supprimez un compte existant pour en ajouter un nouveau.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSwitcher;
